'use client';

import { cn } from '../../lib/utils';
import { bodyPaths } from './legacyPaths';

const PE_AUTO = { pointerEvents: 'auto' } as const;

/** Torso positioning within the full body viewBox */
const TORSO_POSITION = { left: '14.8%', top: '58.7%', width: '70.7%', height: '26.9%' };

interface LegacyRainaBodyProps {
  svgId: string;
  isWaving?: boolean;
  idleAnimations?: boolean;
  onWaveEnd?: () => void;
}

export function LegacyRainaBody({ svgId, isWaving = false, idleAnimations = true, onWaveEnd }: LegacyRainaBodyProps) {
  const id = (name: string) => `${svgId}${name}`;

  return (
    <div className="absolute inset-0 pointer-events-none">
      {/* Right Leg */}
      <div className="absolute inset-[84.23%_38.33%_4.21%_50.36%]">
        <svg aria-hidden="true" className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 48.4597 75.7765">
          <path d={bodyPaths.rightLeg} fill="#33116F" data-region="body" style={PE_AUTO} />
        </svg>
      </div>
      {/* Left Leg */}
      <div className="absolute inset-[84.27%_49.58%_4.16%_39.11%]">
        <svg aria-hidden="true" className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 48.4597 75.7765">
          <path d={bodyPaths.leftLeg} fill="#33116F" data-region="body" style={PE_AUTO} />
        </svg>
      </div>
      {/* Right Shoe */}
      <div className="absolute inset-[93.28%_27.17%_0%_50.38%]">
        <svg aria-hidden="true" className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 96.2058 43.9876">
          <defs>
            <mask id={id('rightShoeMask')} maskUnits="userSpaceOnUse" style={{ maskType: 'alpha' }}>
              <path d={bodyPaths.rightShoeShape} fill="#5329B6" />
            </mask>
          </defs>
          <g mask={`url(#${id('rightShoeMask')})`}>
            <path d={bodyPaths.rightShoeShape} fill="#762BFA" data-region="body" style={PE_AUTO} />
            <rect fill="#33116F" height="22.9992" width="122.662" x="-14.5678" y="33.7324" data-region="body" style={PE_AUTO} />
            <path d={bodyPaths.rightShoeLace1} fill="#33116F" data-region="body" style={PE_AUTO} />
            <path d={bodyPaths.rightShoeLace2} fill="#33116F" data-region="body" style={PE_AUTO} />
          </g>
        </svg>
      </div>
      {/* Left Shoe */}
      <div className="absolute flex inset-[93.28%_49.53%_0%_28.02%] items-center justify-center">
        <div className="size-full rotate-180 -scale-y-100">
          <svg aria-hidden="true" className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 96.2057 43.9876">
            <defs>
              <mask id={id('leftShoeMask')} maskUnits="userSpaceOnUse" style={{ maskType: 'alpha' }}>
                <path d={bodyPaths.leftShoeShape} fill="#5329B6" />
              </mask>
            </defs>
            <g mask={`url(#${id('leftShoeMask')})`}>
              <path d={bodyPaths.leftShoeShape} fill="#762BFA" data-region="body" style={PE_AUTO} />
              <rect fill="#33116F" height="22.9992" width="122.662" x="-14.5677" y="33.7314" data-region="body" style={PE_AUTO} />
              <path d={bodyPaths.leftShoeLace1} fill="#33116F" data-region="body" style={PE_AUTO} />
              <path d={bodyPaths.leftShoeLace2} fill="#33116F" data-region="body" style={PE_AUTO} />
            </g>
          </svg>
        </div>
      </div>
      {/* Torso */}
      <div
        className={cn('absolute', idleAnimations && 'motion-safe:animate-[raina-breathe_4s_ease-in-out_infinite]')}
        style={{ ...TORSO_POSITION, transformOrigin: 'bottom center', overflow: 'visible' }}
      >
        <svg
          aria-hidden="true"
          className="block size-full overflow-visible"
          fill="none"
          preserveAspectRatio="none"
          viewBox="0 0 302.669 175.664"
        >
          <defs>
            <linearGradient gradientUnits="userSpaceOnUse" id={id('leftArmGrad')} x1="63.4732" x2="103.345" y1="64.4469" y2="88.5471">
              <stop stopColor="#BD98FF" />
              <stop offset="1" stopColor="#762BFA" />
            </linearGradient>
            <linearGradient gradientUnits="userSpaceOnUse" id={id('rightArmGrad')} x1="239.202" x2="199.331" y1="64.1911" y2="88.2912">
              <stop stopColor="#BD98FF" />
              <stop offset="1" stopColor="#762BFA" />
            </linearGradient>
            <radialGradient
              cx="0"
              cy="0"
              gradientTransform="translate(152.564 92.8829) rotate(90) scale(67.4644 224.119)"
              gradientUnits="userSpaceOnUse"
              id={id('pocketGrad')}
              r="1"
            >
              <stop stopColor="#BD98FF" />
              <stop offset="1" stopColor="#762BFA" />
            </radialGradient>
            <filter
              colorInterpolationFilters="sRGB"
              filterUnits="userSpaceOnUse"
              height="98.9176"
              id={id('handFilter1')}
              width="84.3514"
              x="9.4659"
              y="67.2279"
            >
              <feFlood floodOpacity="0" result="BackgroundImageFix" />
              <feBlend in="SourceGraphic" in2="BackgroundImageFix" mode="normal" result="shape" />
              <feColorMatrix in="SourceAlpha" result="hardAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" />
              <feOffset dy="-14.5662" />
              <feGaussianBlur stdDeviation="11.5" />
              <feComposite in2="hardAlpha" k2="-1" k3="1" operator="arithmetic" />
              <feColorMatrix type="matrix" values="0 0 0 0 0.79817 0 0 0 0 0.490196 0 0 0 0 0.976471 0 0 0 0.75 0" />
              <feBlend in2="shape" mode="normal" result="effect1_innerShadow" />
            </filter>
            <filter
              colorInterpolationFilters="sRGB"
              filterUnits="userSpaceOnUse"
              height="98.9176"
              id={id('handFilter2')}
              width="84.3514"
              x="208.851"
              y="66.972"
            >
              <feFlood floodOpacity="0" result="BackgroundImageFix" />
              <feBlend in="SourceGraphic" in2="BackgroundImageFix" mode="normal" result="shape" />
              <feColorMatrix in="SourceAlpha" result="hardAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" />
              <feOffset dy="-14.5662" />
              <feGaussianBlur stdDeviation="11.5" />
              <feComposite in2="hardAlpha" k2="-1" k3="1" operator="arithmetic" />
              <feColorMatrix type="matrix" values="0 0 0 0 0.79817 0 0 0 0 0.490196 0 0 0 0 0.976471 0 0 0 0.75 0" />
              <feBlend in2="shape" mode="normal" result="effect1_innerShadow" />
            </filter>
          </defs>
          {/* Left Arm */}
          <g
            className={isWaving ? 'motion-safe:animate-[raina-arm-sway-left_1.2s_ease-out]' : ''}
            style={{ transformBox: 'fill-box', transformOrigin: 'top center' }}
            onAnimationEnd={onWaveEnd}
          >
            <path d={bodyPaths.leftArm} fill={`url(#${id('leftArmGrad')})`} data-region="hand" style={PE_AUTO} />
            <g filter={`url(#${id('handFilter1')})`}>
              <circle
                cx="42.1652"
                cy="42.1652"
                fill="#F6C8BB"
                r="42.1652"
                transform="matrix(-0.965926 0.258819 0.258819 0.965926 81.4569 72.3281)"
                data-region="hand"
                style={PE_AUTO}
              />
            </g>
          </g>
          {/* Right Arm */}
          <g>
            <path d={bodyPaths.rightArm} fill={`url(#${id('rightArmGrad')})`} data-region="hand" style={PE_AUTO} />
            <g filter={`url(#${id('handFilter2')})`}>
              <circle
                cx="251.027"
                cy="123.714"
                fill="#F6C8BB"
                r="42.1652"
                transform="rotate(15 251.027 123.714)"
                data-region="hand"
                style={PE_AUTO}
              />
            </g>
          </g>
          <path d={bodyPaths.torsoBase} fill="#BD98FF" data-region="body" style={PE_AUTO} />
          <path d={bodyPaths.hoodiePocket} fill={`url(#${id('pocketGrad')})`} data-region="body" style={PE_AUTO} />
        </svg>
      </div>
    </div>
  );
}
